import { Bounce, toast, ToastContainer } from "react-toastify";
import styled from "styled-components";
import { TempHot } from "styled-icons/remix-fill";

import { GasEstimateLevels } from "../hooks/useGasEstimates";
import { theme } from "./";

export const ButtonText = styled.button`
	outline: none;
	border: none;
	font-size: inherit;
	padding: 0;
	margin: 0;
	background: none;
	cursor: pointer;

	:hover {
		opacity: 0.7;
	}

	:focus {
		text-decoration: underline;
	}
`

export interface ButtonProps {
	owner?: boolean
	wallet?: boolean
}

export const Button = styled.button<ButtonProps>`
	position: relative;
	padding: 10px;
	border: ${({ owner, theme }) => {
		if (owner) {
			return `2px solid ${theme.owner1}};`
		} else return `none;`
	}}
	background-color: transparent;
	font-weight: 700;
	font-size: 12pt;
	color: ${({ wallet, theme }) => {
		if (wallet) return theme.primary1
		else return theme.white
	}};
	background-color: ${({ owner, wallet, theme }) => {
		if (owner) return `${theme.bg1}};`
		if (wallet) return `${theme.white}};`
		return `${theme.primary1}};`
	}}
	border-radius: ${({ owner, theme }) => (owner && `0.5rem;`) || `0.5rem;`}
	margin: 1rem;
	min-width: fit-content;
	
	:hover,
	:focus {
		cursor: pointer;
		outline: none;
		background-color: ${({ owner, wallet, theme }) => {
			if (owner) {
				return `${theme.owner1}};`
			} else if (wallet) {
				return `${theme.bg5}`
			} else return `${theme.primary1}};`
		}}
	}

	:hover {
		${({ wallet, theme }) => {
			if (wallet) return `color: ${theme.white}`
		}}
	}

	svg {
		margin-top: 2px;
	}
	> * {
		stroke: ${({ theme }) => theme.text1};
	}

	box-shadow: ${({ theme }) => theme.bg1} 0px 1px 2px 0px, ${({ theme }) =>
	theme.bg1} 0px 2px 6px 2px;
`

export const PageWrapper = styled.div`
	display: flex;
	flex-direction column;
	align-items: center;
	border-sizing: border-box;
	
	${({ theme }) => theme.mediaWidth.upToMedium`
	margin: 0 0 6rem 0;
	padding: 0 0.5rem 0 0.5rem;
  `};
`

export interface DashboardWrapperProps {
	noTheme?: boolean
	intro?: boolean
}
export const DashboardWrapper = styled.div<DashboardWrapperProps>`
	border-sizing: border-box;
	margin-bottom: 1rem;
	padding: 2rem;
	display: flex;
	flex-direction: column;
	align-items: center;
	overflow: hidden;
	border-radius: 16px;

	${({ intro, noTheme, theme }) =>
		!noTheme &&
		!intro &&
		`
	background-color: ${theme.bg2};
	box-shadow: ${theme.bg0} 0px 4px 12px;
	`};

	${({ intro, noTheme, theme }) =>
		intro &&
		!noTheme &&
		`
    padding: 0.5rem 0 !important;
    border: 0.5rem solid black;
	  // background-color: ${theme.black} !important;
	  box-shadow: ${theme.bg0} 0px 4px 12px;
	`};

	min-width: 100%;
	max-width: 800px;

	${({ theme }) => theme.mediaWidth.upToSmall`
		max-width: 100vw;
  `};
`

export interface InterfaceProps {
	justifyContent?: string
	responsive?: boolean
	col?: boolean
}

export const Interface = styled.div<InterfaceProps>`
	min-width: fit-content;
	width: 100%;
	padding: 0.5rem;
	display: flex;
	flex-flow: ${({ col }) => (col ? 'column nowrap' : `row nowrap`)};
	justify-content: ${({ justifyContent }) => justifyContent ?? 'space-evenly'};
	align-items: center;
	border-sizing: border-box;

	${({ responsive, theme }) => {
		if (!responsive)
			return theme.mediaWidth.upToSmall`
				flex-direction: column;
			`
	}};
`

export const ElementWrapper = styled.div`
	margin: 0;
	padding: 0;
`

export interface TitleProps {
	fontSize?: number
	color1?: any
	color2?: any
	color3?: any

	primary1?: any
	primary2?: any
	primary3?: any
}
export const Title = styled.span<TitleProps>`
	font-size: ${({ fontSize }) => (fontSize ? `${fontSize}pt` : '22pt')};
	font-weight: bold;
	color: ${({
		color1,
		color2,
		color3,
		primary1,
		primary2,
		primary3,
		theme,
	}) => {
		if (color1) return theme.text1
		else if (color2) return theme.text2
		else if (color3) return theme.text3
		else if (primary1) return theme.primary1
		else if (primary2) return theme.primary2
		else if (primary3) return theme.primary3
		else return theme.text1
	}};
	display: block;
	text-align: center;
	margin: 0.5rem 0 1rem 0;
`

export interface TextProps {
	color1?: any
	color2?: any
	color3?: any
	primary1?: any
	primary2?: any
	primary3?: any
	owner1?: any
	fontSize?: number
	raw?: boolean
	underline?: boolean
	left?: boolean
	right?: boolean
	indent?: boolean
	gasEstimate?: string
}

export const Text = styled.span<TextProps>`
	overflow-wrap: anywhere;
	font-size: ${({ fontSize }) => (fontSize ? `${fontSize}pt` : '14pt')};
	margin: ${({ raw, indent }) =>
		raw ? `0rem` : indent ? '0 0 0 1rem' : '1rem'};
	color: ${({ theme }) => theme.text2};
	text-align: ${({ left, right }) => {
		if (left) return 'left'
		else if (right) return 'right'
		else return 'center'
	}};
	text-wrap: wrap;
	color: ${({
		color1,
		color2,
		color3,
		primary1,
		primary2,
		primary3,
		owner1,
		theme,
		gasEstimate,
	}) => {
		if (color1) return theme.text1
		else if (color2) return theme.text2
		else if (color3) return theme.text3
		else if (primary1) return theme.primary1
		else if (primary2) return theme.primary2
		else if (primary3) return theme.primary3
		else if (owner1) return theme.owner1
		else if (gasEstimate) {
			switch (gasEstimate as GasEstimateLevels) {
				case GasEstimateLevels.Low:
					return theme.owner1
				case GasEstimateLevels.Market:
					return theme.avg
				case GasEstimateLevels.High:
					return theme.high
			}
		} else return theme.text2
	}};

	text-decoration: ${({ underline }) => (underline ? 'underline' : 'none')};
`

export const ExternalLink = styled.a`
	max-width: 50px;
	text-overflow: ellipsis;
	overflow-wrap: anywhere;
	font-size: 14pt;
	color: ${({ theme }) => theme.primary1};
	text-align: center;

	:hover {
		color: ${({ theme }) => theme.primary2};
		cursor: pointer;
	}
`

interface IStyledLink {
	size?: string
	raw?: boolean
}
export const StyledLink = styled.a<IStyledLink>`
	width: fit-content;
	color: ${({ theme }) => theme.primary1};
	font-size: ${({ size }) => (size ? `${size}pt` : '14pt')};
	margin: ${({ raw }) => (raw ? `0 0 0 1rem` : '1rem')};
	text-align: center;
	:hover {
		color: ${({ theme }) => theme.primary2};
		cursor: pointer;
	}
`

interface IEmoji {
	fontSize?: string
}
export const Emoji = styled.span<IEmoji>`
	font-size: ${({ fontSize }) => fontSize ?? '24pt'};
	transition: transform 0.3s ease;
	:hover {
		cursor: pointer;
		transform: rotate(-5deg);
	}
`

interface InputBoxProps {
	grow?: boolean
}
export const InputBox = styled.input<InputBoxProps>`
	flex-grow: ${({ grow }) => (grow ? 1 : 0)};
	font-size: 18px;
	padding: 10px;
	margin: 10px;
	border: none;
	border-radius: 3px;
`

export const StyledToastContainer = styled(ToastContainer).attrs({
	className: 'toast-container',
	toastClassName: 'toast',
	bodyClassName: 'body',
	progressClassName: 'progress',
	position: toast.POSITION.BOTTOM_RIGHT,
})`
	/* .toast-container */
	min-width: fit-content;

	/* .toast is passed to toastClassName */
	.toast {
		font-family: 'San Francisco';
		color: ${({ theme }) => theme.text1};
		background-color: ${({ theme }) => theme.bg4};
		box-shadow: ${({ theme }) => theme.bg1} 0px 1px 2px 0px,
			${({ theme }) => theme.bg1} 0px 2px 6px 2px;
	}

	button[aria-label='close'] {
		display: none;
	}

	/* .body is passed to bodyClassName */
	.body {
	}

	/* .progress is passed to progressClassName */
	.progress {
		background-color: ${({ theme }) => theme.primary1};
	}
`
export const LandingImage = styled.img`
	width: 100%;
	max-width: 1000px;
	height: auto;
	border-sizing: border-box;
	margin: 0 0 2rem 0;
	filter: drop-shadow(15px 15px 15px ${({ theme }) => theme.primary5});
	-webkit-mask-image: -webkit-gradient(
		linear,
		left top,
		left bottom,
		from(rgba(0, 0, 0, 1)),
		to(rgba(0, 0, 0, 0))
	);
	mask-image: linear-gradient(
		to bottom,
		rgba(0, 0, 0, 1),
		90%,
		rgba(0, 0, 0, 0)
	);
`

export const SelectorInterface = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	box-sizing: border-box;
	min-width: 65%;

	${({ theme }) => theme.mediaWidth.upToMedium`
		justify-content: space-around;
	`};

	${({ theme }) => theme.mediaWidth.upToSmall`
		flex-direction: column;
		justify-content: center;
  	`};
`

export interface SelectorButtonProps {
	selected: boolean
}

export const SelectorButton = styled.button<SelectorButtonProps>`
	background: ${({ theme, selected }) =>
		selected ? theme.primary4 : theme.bg2};
	color: ${({ theme, selected }) => (selected ? theme.primary1 : theme.text1)};
	border: none;
	text-decoration: none;
	padding: 0.5rem;
	box-sizing: border-box;
	border-radius: 1rem;
	min-width: fit-content;
	justify-content: center;

	display: flex;
	flex-direction: column;
	align-items: center;

	:only-child {
		margin-left: auto;
		margin-right: auto;
		display: none;
	}

	:hover {
		cursor: pointer;
		user-select: none;
		background: ${({ theme }) => theme.primary5};
	}

	${({ theme }) => theme.mediaWidth.upToSmall`
		margin: 1rem 0rem;
  `};
`
